import { AudioManager } from '@dvb/common/src/classes';
import { taskDetailQueryOptions } from '@school-manage/api/actions/tasks/tasks.options';
import { AUDIO_ASSETS_MAP } from '@school-manage/common/constants';
import { TaskPathSchema } from '@school-manage/schema/path.schema';
import { warnIfOfflineNoCache } from '@school-manage/utils/offline';
import { toast } from '@school-manage/utils/toast';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';

import { SportDetailError } from './-components/error/SportDetailError';
import { SportSwitcher } from './-components/SportSwitcher';
import { getTaskStatusError } from './-logic/task-status';
import { SportSwitcherContext } from './-logic/useSportSwitcherContext';

export const Route = createFileRoute('/_authed/task/$taskId_/sport')({
  beforeLoad: ({ context: { queryClient }, location, params: { taskId } }) => {
    warnIfOfflineNoCache(location, [
      queryClient.getQueryData(['sports']),
      queryClient.getQueryData(['task-detail', { taskId }]),
    ]);
  },
  component: SportPage,
  errorComponent: SportDetailError,
  loader: async ({ context: { queryClient }, params: { taskId } }) => {
    const data = await queryClient.ensureQueryData(
      taskDetailQueryOptions(taskId)
    );
    const errorMsg = getTaskStatusError(data);
    if (errorMsg) {
      toast.error(errorMsg);
      throw redirect({
        params: { taskId },
        to: '/task/$taskId',
      });
    }
  },
  params: {
    parse: TaskPathSchema.parse,
  },
});

/**
 * For the first approach, we have sport detail and sport game in a sperate page,
 * but it facing audio auto play issue in Chrome. So we combine them into a single page
 * and use a state to switch between them as a workaround solution.
 */
function SportPage() {
  const [isStartGame, setIsStartGame] = useState(false);

  const startGame = useCallback(() => {
    AudioManager.initAudio(Object.keys(AUDIO_ASSETS_MAP).length);
    setIsStartGame(true);
  }, []);

  const sport = useMemo(
    () => ({ isStartGame, startGame }),
    [isStartGame, startGame]
  );

  return (
    <SportSwitcherContext.Provider value={sport}>
      <SportSwitcher />
    </SportSwitcherContext.Provider>
  );
}
